<script setup lang="ts">
import {
    faBus,
    faCircleInfo,
    faClock,
    faPlane,
    faTaxi,
    faTrain,
} from '@fortawesome/pro-regular-svg-icons';
import { faPlaneCircleExclamation } from '@fortawesome/pro-solid-svg-icons';
import VehicleImages from './VehicleImages.vue';
import type Trip from '@itinerary/trip/Trip';
import { VehicleType } from '@itinerary/trip/types';
import TripCardLogo from '@components/util/trip/TripCardLogo.vue';

interface IProps {
    trip: Trip;
    isFirst?: boolean;
    compact?: boolean;
    hasFinalDestination?: boolean;
    highlight?: boolean;
}
const { trip, compact, isFirst, hasFinalDestination } = defineProps<IProps>();
const departureIcon = computed(() => {
    switch (trip.vehicleType) {
        case VehicleType.Plane:
            return {
                icon: faPlane,
                rotation: '90' as const,
            };
        case VehicleType.Train:
            return {
                icon: faTrain,
            };
        case VehicleType.Bus:
            return {
                icon: faBus,
            };
        case VehicleType.Taxi:
            return {
                icon: faTaxi,
            };
        default:
            return {
                icon: undefined,
            };
    }
});
const durationTime = computed(() => secondsToDurationTime(trip.duration));
const originTitle = computed(() => {
    if (trip.originCityName.includes(trip.originTerminal)) {
        return trip.originCityName;
    }
    return `${trip.originCityName}، ${trip.originTerminal}`;
});
const destinationTitle = computed(() => {
    if (
        trip.destinationCityName.includes(trip.destinationTerminal) ||
        hasFinalDestination
    ) {
        return trip.destinationCityName;
    }
    return `${trip.destinationCityName}، ${trip.destinationTerminal}`;
});
</script>

<template>
    <div class="trip-view-container" :class="{ highlight }">
        <div class="times-section" :class="{ compact }">
            <mr-typography weight="500" class="time">
                {{ trip.departureTime }}
            </mr-typography>
            <div v-if="!compact" class="time duration">
                <mr-typography weight="500"> {{ durationTime }}</mr-typography>
                <mr-icon :icon="faClock" color="var(--cool-gray-600)" />
            </div>
            <div class="time">
                <mr-typography weight="500">
                    {{ trip.arrivalTime }}
                </mr-typography>
                <mr-icon
                    v-show="trip.destinationHasWarning"
                    :icon="faCircleInfo"
                    size="x-small"
                />
            </div>
        </div>
        <div class="time-line-section">
            <mr-icon
                v-if="isFirst && departureIcon.icon"
                size="small"
                :icon="departureIcon.icon"
                :rotation="departureIcon.rotation"
                color="var(--cool-gray-400)"
            />
            <span v-else class="circle-icon" />
            <mr-divider vertical class="divider" />
            <template
                v-for="mIndex in trip.tripMidStops.length"
                :key="`mid-circle-${mIndex - 1}`"
            >
                <span class="circle-icon" />
                <mr-divider vertical class="mid-divider" size="10px" />
            </template>
            <span class="circle-icon" />
        </div>
        <div class="info-section">
            <div class="title orig">
                {{ originTitle }}
            </div>
            <div class="trip-info">
                <TripCardLogo
                    :url="trip.corporationInfo.logoUrl"
                    desktop-small
                    :title="trip.corporationInfo.title"
                    compact
                />
                <mr-typography color="var(--cool-gray-600)" class="info-title">
                    {{ trip.corporationInfo.title }}
                    {{ trip.corporationInfo.subTitle || '' }}
                </mr-typography>
                <template v-if="trip.tripExtraInfo?.length">
                    <template
                        v-for="(info, i) in trip.tripExtraInfo"
                        :key="`divider-${i}`"
                    >
                        <mr-divider
                            class="mr-divider"
                            vertical
                            size="12px"
                            color="var(--cool-gray-400)"
                        />
                        <mr-typography
                            color="var(--cool-gray-600)"
                            class="info-title"
                        >
                            {{ info }}
                        </mr-typography>
                    </template>
                </template>
                <VehicleImages v-if="!compact" :trip />
                <div v-if="trip.operatingWarning" class="operating-warning">
                    <mr-icon
                        :icon="faPlaneCircleExclamation"
                        color="var(--purple-400)"
                        size="small"
                    />
                    <div class="description">
                        {{ trip.operatingWarning }}
                    </div>
                </div>
            </div>
            <mr-flash-message
                v-if="trip.technicalStops.length"
                :items="trip.technicalStops"
            />
            <template v-for="mid in trip.tripMidStops" :key="mid">
                <div class="title mid">
                    {{ mid }}
                </div>
            </template>
            <div
                class="title dest"
                :class="{ 'with-mid': trip.tripMidStops.length }"
            >
                {{ destinationTitle }}
            </div>
        </div>
    </div>
</template>

<style lang="scss" scoped>
.trip-view-container {
    display: flex;
    align-items: stretch;
    border-radius: 12px;
    &.highlight {
        border-radius: 12px;
        padding: 8px 4px;
        background-color: usePalette($p-gray, $p-100);
        @include desktop-only {
            background-color: usePalette($p-gray, $p-200);
        }
    }
}

.times-section {
    flex-shrink: 0;
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    justify-content: space-between;
    width: 62px;
    &.compact {
        width: 40px;
    }
    .time {
        display: flex;
        align-items: center;
        &.duration {
            color: usePalette($p-cool-gray, 600);
            margin: 8px 0;
        }
    }
}

.time-line-section {
    flex: 0 0 20px;
    display: flex;
    flex-direction: column;
    align-items: center;
    margin: 0 20px;
    padding: 8px 0;
    .circle-icon {
        width: 10px;
        height: 10px;
        border-radius: 100%;
        background-color: usePalette($p-cool-gray, 400);
    }
    .divider {
        flex: 1;
        margin: 8px 0;
    }
    .mid-divider {
        margin: 8px 12px;
    }
}

.info-section {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    .title {
        @include tg-m($w5);
        &:not(:last-child) {
            margin-bottom: -4px;
        }
        &.with-mid {
            margin-top: 8px;
        }
    }
    .trip-info {
        flex: 1;
        display: flex;
        align-items: center;
        flex-wrap: wrap;
        margin: 8px 0;
        .mr-divider {
            margin: 12px;
        }
    }
    .info-title {
        margin-right: 4px;
    }
    .operating-warning {
        display: flex;
        padding: 4px;
        border-radius: 8px;
        .description {
            @include tg-s($w7, usePalette($p-purple));
            margin-right: 4px;
        }
    }
}
</style>
